import React from 'react';
import { Users, CheckCircle, XCircle } from 'lucide-react';
import { getLocalizedField } from '../../utils/localizedContent';

const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"];

export function DateTimeSelector({
  selectedDate,
  setSelectedDate,
  selectedSlot,
  setSelectedSlot,
  duration,
  setDuration,
  partySize,
  setPartySize,
  setSelectedTables = () => {},
  t,
  lang,
  waitForSlotsEnabled = false,
  slotsEnabled = true,
  slotDisabled,
  slotDetail,
  slotClassName,
  partySizeMin = 1,
  partySizeMax = 10,
  dateTestId = 'table-date-picker',
  durationTestId = 'table-duration',
  partySizeTestId = 'party-size',
}) {
  const slotsInteractive = !waitForSlotsEnabled || slotsEnabled;

  return (
    <div className="space-y-6">
      {/* Date Picker */}
      <div>
        <h3 className="text-[#F5F0E6] font-bold mb-3 text-sm uppercase tracking-wider text-[#C9A962]">
          {t('reservations.select_date')}
        </h3>
        <input 
          type="date" 
          value={selectedDate} 
          onChange={e => { setSelectedDate(e.target.value); setSelectedTables([]); setSelectedSlot(''); }}
          min={new Date().toISOString().split('T')[0]}
          className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-3 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
          data-testid={dateTestId}
        />
      </div>
      
      {/* Time Slots */}
      {selectedDate && (
        <div>
          <h3 className="text-[#F5F0E6] font-bold mb-3 text-sm uppercase tracking-wider text-[#C9A962]">
            {t('reservations.select_slot')}
          </h3>
          {waitForSlotsEnabled && !slotsEnabled ? (
            <p className="text-[#A8B5AE] text-xs mb-2">{t('reservations.select_game')}</p>
          ) : null}
          <div className="grid grid-cols-3 gap-2">
            {TIME_SLOTS.map(slot => {
              const unavailable = typeof slotDisabled === 'function' ? slotDisabled(slot) : false;
              const disabled = !slotsInteractive || unavailable;
              const detail = typeof slotDetail === 'function' ? slotDetail(slot) : null;
              const extra = typeof slotClassName === 'function' ? slotClassName(slot) : '';
              const baseLayout = detail ? 'py-2 px-1 flex flex-col items-center gap-0.5' : 'py-2';
              return (
                <button 
                  key={slot} 
                  type="button"
                  onClick={() => {
                    if (disabled) return;
                    setSelectedSlot(slot);
                    setSelectedTables([]);
                  }}
                  disabled={disabled}
                  className={`${baseLayout} rounded-xl text-xs font-semibold border transition-all ${
                    selectedSlot === slot 
                      ? 'status-selected border-[#C9A962]/50' 
                      : disabled
                        ? 'bg-[#EF4444]/10 border-[#EF4444]/30 text-[#EF4444] opacity-60 cursor-not-allowed'
                        : `bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6] ${extra}`.trim()
                  }`}
                  data-testid={`time-slot-${slot}`}
                >
                  <span>{slot}</span>
                  {detail ? (
                    <span className={`text-[9px] font-normal ${selectedSlot === slot ? '' : disabled ? 'text-[#EF4444]/80' : 'text-[#A8B5AE]'}`}>
                      {detail}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Duration and Party Size */}
      {selectedSlot && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-[#F5F0E6] text-sm font-semibold block mb-1">
              {t('reservations.duration')}
            </label>
            <select 
              value={duration} 
              onChange={e => { setDuration(Number(e.target.value)); setSelectedTables([]); }}
              className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-2 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
              data-testid={durationTestId}
            >
              <option value={1}>1h</option>
              <option value={2}>2h</option>
              <option value={3}>3h</option>
            </select>
          </div>
          <div>
            <label className="text-[#F5F0E6] text-sm font-semibold block mb-1">
              {t('reservations.party_size')}
            </label>
            <input 
              type="number" 
              value={partySize} 
              onChange={e => setPartySize(Math.max(partySizeMin, Math.min(partySizeMax, Number(e.target.value) || partySizeMin)))} 
              min={partySizeMin} 
              max={partySizeMax}
              className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-2 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962]"
              data-testid={partySizeTestId}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function TableSelector({
  selectedSlot,
  selectedTables,
  availability,
  isTableAvailableForDuration,
  toggleTable,
  partySize,
  getTotalCapacity,
  notes,
  setNotes,
  t,
  lang
}) {
  if (!selectedSlot || !availability) {
    return (
      <p className="text-[#A8B5AE] text-sm">
        {t('reservationsShared.select_date_first')}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-[#F5F0E6] font-bold mb-3 text-sm uppercase tracking-wider text-[#C9A962]">
        {t('reservationsShared.select_tables')}
      </h3>
      
      {/* Tables Grid */}
      <div className="grid grid-cols-2 gap-3">
        {availability.tables?.map(table => {
          const isAvailable = isTableAvailableForDuration(table.table_number);
          const isSelected = selectedTables.includes(table.table_number);
          return (
            <TableCard
              key={table.table_number}
              table={table}
              isAvailable={isAvailable}
              isSelected={isSelected}
              onToggle={() => toggleTable(table.table_number)}
              lang={lang}
            />
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs mt-3">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-[#C9A962]/30 border border-[#C9A962]"></div>
          <span className="text-[#A8B5AE]">{t('reservationsShared.selected')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-[#2D8B70]/20 border border-[#2D8B70]/30"></div>
          <span className="text-[#A8B5AE]">{t('reservations.available')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-[#C45B4A]/20 border border-[#C45B4A]/30"></div>
          <span className="text-[#A8B5AE]">{t('reservations.booked')}</span>
        </div>
      </div>
      
      {/* Capacity Summary */}
      {selectedTables.length > 0 && (
        <CapacitySummary 
          totalCapacity={getTotalCapacity()} 
          partySize={partySize} 
          lang={lang} 
        />
      )}
      
      {/* Notes */}
      <div>
        <label className="text-[#F5F0E6] text-sm font-semibold block mb-1">
          {t('reservations.notes')} ({t('common.optional')})
        </label>
        <textarea 
          value={notes} 
          onChange={e => setNotes(e.target.value)} 
          rows={2}
          className="w-full bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl px-4 py-2 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] resize-none placeholder:text-[#A8B5AE]/50"
          placeholder={t('reservationsShared.optional_notes_example')}
          data-testid="reservation-notes"
        />
      </div>
    </div>
  );
}

function TableCard({ table, isAvailable, isSelected, onToggle, lang }) {
  return (
    <button 
      onClick={onToggle}
      disabled={!isAvailable}
      className={`p-4 rounded-xl border transition-all text-left ${
        isSelected 
          ? 'border-[#C9A962] bg-[#C9A962]/15' 
          : isAvailable 
            ? 'border-[#2D8B70]/30 bg-[#0F1F1A] hover:border-[#C9A962]/40' 
            : 'border-[#C45B4A]/20 bg-[#C45B4A]/5 opacity-50 cursor-not-allowed'
      }`}
      data-testid={`table-${table.table_number}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-lg font-bold text-[#F5F0E6]">#{table.table_number}</span>
        {isSelected ? (
          <CheckCircle size={18} className="text-[#C9A962]" />
        ) : !isAvailable ? (
          <XCircle size={18} className="text-[#C45B4A]" />
        ) : null}
      </div>
      <p className="text-xs text-[#A8B5AE] truncate">{getLocalizedField(table, 'name', lang, table.name)}</p>
      <div className="flex items-center gap-1 mt-1 text-xs">
        <Users size={12} className="text-[#C9A962]" />
        <span className="text-[#F5F0E6] font-semibold">
          {table.capacity} {t('reservationsShared.people')}
        </span>
      </div>
    </button>
  );
}

function CapacitySummary({ totalCapacity, partySize, lang }) {
  const isEnough = totalCapacity >= partySize;
  
  return (
    <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#A8B5AE]">
          {t('reservationsShared.total_capacity_label')}
        </span>
        <span className={`text-sm font-bold ${isEnough ? 'text-[#2D8B70]' : 'text-[#C45B4A]'}`}>
          {totalCapacity} / {partySize} {t('reservationsShared.people')}
        </span>
      </div>
      {!isEnough && (
        <p className="text-xs text-[#C45B4A] mt-1">
          {t('reservationsShared.select_more_tables')}
        </p>
      )}
    </div>
  );
}
